import addObligationBasisLineModal from '../addObligationBasisLineModal/addObligationBasisLineModal.component';

class ManageObligationBasisLinesModalController {
  constructor(
    $uibModalInstance,
    $uibModal,
    $http,
    $rootScope,
    $q,
    toastr,
    project,
    obligation,
    obligationLabel,
    canManage,
    helpers
  ) {
    'ngInject';

    this.$uibModalInstance = $uibModalInstance;
    this.$uibModal = $uibModal;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.$q = $q;
    this.toastr = toastr;
    this.project = project || {};
    this.obligation = obligation || {};
    this.obligationLabel = obligationLabel || '';
    this.canManage = !!canManage;
    this.helpers = helpers || {};

    this.loading = false;
    this.balancing = false;
    this.savingByLineId = {};
    this.basisLines = [];
    this.changed = false;
    this.sort = {
      field: 'laborCategoryName',
      reverse: false
    };
    this.orderBy = (line) => this.sortValue(line, this.sort.field);

    this.loadBasisLines();
  }

  formatMoney(value) {
    if (typeof this.helpers.formatMoney === 'function') {
      return this.helpers.formatMoney(value);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 5
    }).format(Number(value) || 0);
  }

  formatHours(value) {
    if (typeof this.helpers.formatHours === 'function') {
      return this.helpers.formatHours(value);
    }
    const num = Number(value);
    if (!isFinite(num)) {
      return '0.00';
    }
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 5
    });
  }

  basisRateTypeLabel(line) {
    if (typeof this.helpers.basisRateTypeLabel === 'function') {
      return this.helpers.basisRateTypeLabel(line);
    }
    if (!line) {
      return '';
    }
    if (line.rateType === 'FIXED_FEE') {
      return 'Fixed Fee / Hour';
    }
    if (line.rateType === 'FULLY_LOADED_LABOR') {
      return 'Fully Loaded Labor';
    }
    return line.rateType || '';
  }

  basisLinesUrl() {
    return `${this.$rootScope.apiHost}/api/admin/contracts/${this.project.contractId}`
      + `/funding-obligations/${this.obligation.id}/basis-lines`;
  }

  loadBasisLines() {
    if (!this.project.contractId || !this.obligation.id) {
      this.basisLines = [];
      return;
    }

    this.loading = true;
    this.$http.get(this.basisLinesUrl(), { withCredentials: true }).then((success) => {
      this.basisLines = success.data || [];
    }, (failure) => {
      this.basisLines = [];
      const message = (failure && failure.data && failure.data.message) || 'Failed to load basis lines';
      this.toastr.error(message, 'ERROR');
    }).finally(() => {
      this.loading = false;
    });
  }

  sortBasisLines(field, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (this.sort.field === field) {
      this.sort.reverse = !this.sort.reverse;
    } else {
      this.sort.field = field;
      this.sort.reverse = false;
    }
  }

  sortIcon(field) {
    if (this.sort.field !== field) {
      return 'fa-sort';
    }
    return this.sort.reverse ? 'fa-sort-desc' : 'fa-sort-asc';
  }

  sortValue(line, field) {
    if (!line) {
      return null;
    }
    if (field === 'laborCategoryName') {
      return (line.laborCategoryName == null || line.laborCategoryName === ''
        ? ''
        : String(line.laborCategoryName)).toLowerCase();
    }
    const num = Number(line[field]);
    return isFinite(num) ? num : 0;
  }

  hoursTotal() {
    const total = (this.basisLines || [])
      .reduce((sum, line) => sum + (Number(line.fundedHours) || 0), 0);
    return Math.round(total * 100) / 100;
  }

  amountTotal() {
    const total = (this.basisLines || [])
      .reduce((sum, line) => sum + (Number(line.fundedAmount) || 0), 0);
    return Math.round(total * 100) / 100;
  }

  isLineSaving(lineId) {
    return !!this.savingByLineId[lineId];
  }

  canBalance() {
    return this.canManage
      && (this.basisLines || []).length > 0
      && !this.loading
      && !this.balancing;
  }

  balanceHours() {
    if (!this.canBalance()) {
      return;
    }

    const obligated = this.formatMoney(this.obligation.obligatedAmount);
    const confirmed = window.confirm(
      'Balance funded hours across basis lines so total funded amount equals '
      + obligated + '?\n\n'
      + 'Existing hours will be scaled proportionally. If all hours are zero, '
      + 'the obligated amount is split evenly.'
    );
    if (!confirmed) {
      return;
    }

    this.balancing = true;
    this.$http.post(
      `${this.basisLinesUrl()}/balance`,
      {},
      { withCredentials: true }
    ).then((success) => {
      this.basisLines = success.data || [];
      this.changed = true;
      this.toastr.success('Funded hours balanced to obligated amount');
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message)
        || 'Failed to balance funded hours';
      this.toastr.error(message, 'ERROR');
    }).finally(() => {
      this.balancing = false;
    });
  }

  openAddBasisLine() {
    if (!this.canManage) {
      return;
    }

    const self = this;
    const modalInstance = this.$uibModal.open({
      template: addObligationBasisLineModal.template,
      controller: addObligationBasisLineModal.controller,
      controllerAs: '$ctrl',
      size: addObligationBasisLineModal.size || 'md',
      windowClass: addObligationBasisLineModal.windowClass || 'hmodal-info animated zoomIn',
      resolve: {
        contractId: function () {
          return self.project.contractId;
        },
        obligation: function () {
          return self.obligation;
        },
        obligationLabel: function () {
          return self.obligationLabel;
        }
      }
    });

    modalInstance.result.then(function () {
      self.changed = true;
      self.loadBasisLines();
    }, function () {
      // dismissed
    });
  }

  updateHours(line, newHours) {
    if (!this.canManage || !line || !line.id) {
      return this.$q.reject('Cannot update funded hours');
    }

    let raw = newHours;
    if (raw === undefined) {
      raw = line.fundedHours;
    }
    if (raw === null || raw === '') {
      return this.$q.reject('Funded hours is required');
    }

    const hours = typeof raw === 'number'
      ? raw
      : parseFloat(String(raw).replace(/,/g, ''), 10);
    if (!isFinite(hours) || hours < 0) {
      return this.$q.reject('Funded hours must be a non-negative number');
    }

    const roundedHours = Math.round(hours * 100000) / 100000;
    this.savingByLineId[line.id] = true;
    return this.$http.patch(
      `${this.basisLinesUrl()}/${line.id}`,
      { fundedHours: roundedHours },
      { withCredentials: true }
    ).then((success) => {
      const updated = success.data || {};
      line.fundedHours = updated.fundedHours != null ? updated.fundedHours : roundedHours;
      line.fundedAmount = updated.fundedAmount;
      line.rate = updated.rate;
      this.changed = true;
      this.toastr.success('Funded hours updated');
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message) || 'Failed to update funded hours';
      this.toastr.error(message, 'ERROR');
      this.loadBasisLines();
      return this.$q.reject(message);
    }).finally(() => {
      this.savingByLineId[line.id] = false;
    });
  }

  deleteLine(line) {
    if (!this.canManage || !line || !line.id) {
      return;
    }
    if (!window.confirm('Remove this fixed fee basis line from the obligation?')) {
      return;
    }

    this.savingByLineId[line.id] = true;
    this.$http.delete(
      `${this.basisLinesUrl()}/${line.id}`,
      { withCredentials: true }
    ).then(() => {
      this.toastr.success('Basis line removed');
      this.changed = true;
      this.loadBasisLines();
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message) || 'Failed to remove basis line';
      this.toastr.error(message, 'ERROR');
    }).finally(() => {
      this.savingByLineId[line.id] = false;
    });
  }

  close() {
    if (this.changed) {
      this.$uibModalInstance.close({ changed: true });
      return;
    }
    this.$uibModalInstance.dismiss('cancel');
  }
}

export default ManageObligationBasisLinesModalController;



// WEBPACK FOOTER //
// ./client/common/modals/manageObligationBasisLinesModal/manageObligationBasisLinesModal.controller.js